import { colors } from '../../../theme/tokens/colors';
import { chat as defaultAccent } from '../../../theme/tokens/semantic';

export function createProfileSx(tokenOverrides = {}) {
  const accent = tokenOverrides.accent ?? defaultAccent;

  return {
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100vh - 80px)',
    },
    orgFilter: {
      minWidth: { xs: '100%', sm: 220 },
    },
    permissionsPageRoot: {
      width: '100%',
      flex: 1,
      minHeight: 'calc(100vh - 80px)',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      px: { xs: 2, md: 3 },
      py: 2.5,
    },
    permissionsPageHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    },
    permissionsTitle: {
      fontWeight: 700,
      color: accent.textPrimary ?? colors.grey[900],
    },
    permissionsSubtitle: {
      color: accent.textSecondary ?? colors.grey[700],
      mb: 0.5,
    },
    permissionMatrixSection: {
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: 0,
      overflow: 'hidden',
      bgcolor: colors.white,
    },
    permissionMatrixSectionTitle: {
      px: 2.5,
      py: 1.75,
      fontWeight: 700,
      color: accent.textPrimary ?? colors.grey[900],
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
    },
    permissionMatrixScroll: {
      overflowX: 'auto',
    },
    permissionMatrixHeaderRow: {
      display: 'grid',
      alignItems: 'center',
      columnGap: 1,
      px: 2,
      py: 1.25,
      minWidth: { xs: 640, md: 760, lg: 920 },
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      bgcolor: colors.grey[50],
    },
    permissionMatrixHeaderSpacer: {
      minWidth: 0,
    },
    permissionMatrixColumnHeader: {
      textAlign: 'center',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: accent.textSecondary ?? colors.grey[500],
    },
    permissionMatrixRow: {
      display: 'grid',
      alignItems: 'center',
      columnGap: 1,
      px: 2,
      py: 1.35,
      minWidth: { xs: 640, md: 760, lg: 920 },
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      '&:last-child': { borderBottom: 'none' },
      '&:hover': {
        bgcolor: colors.grey[50],
      },
    },
    permissionMatrixRowLabel: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      minWidth: 0,
      pr: 1,
    },
    permissionMatrixRowIcon: {
      mt: 0.15,
      fontSize: 18,
      color: accent.textSecondary ?? colors.grey[600],
      flexShrink: 0,
    },
    permissionTreeNodeToggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.25,
      flexShrink: 0,
    },
    permissionMatrixRowTitle: {
      fontWeight: 500,
      color: accent.textPrimary ?? colors.grey[900],
      lineHeight: 1.4,
    },
    permissionMatrixRowMeta: {
      display: 'block',
      mt: 0.25,
      color: accent.textSecondary ?? colors.grey[500],
    },
    permissionMatrixCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.25,
      minWidth: 0,
    },
    permissionMatrixCheckbox: {
      p: 0.35,
      color: colors.grey[400],
      '&.Mui-checked': {
        color: accent.primary ?? '#2563EB',
      },
    },
    permissionMatrixYes: {
      fontWeight: 500,
      color: accent.textPrimary ?? colors.grey[800],
      minWidth: 24,
    },
    permissionMatrixNo: {
      color: accent.textSecondary ?? colors.grey[500],
      minWidth: 24,
    },
    permissionMatrixInactive: {
      color: accent.textSecondary ?? colors.grey[400],
      textAlign: 'center',
    },
    permissionModuleSection: {
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: 0,
      overflow: 'hidden',
      bgcolor: colors.white,
    },
    permissionModuleHeaderRow: {
      display: 'grid',
      alignItems: 'center',
      columnGap: 1,
      px: 2,
      py: 1.5,
      minWidth: { xs: 640, md: 760 },
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      bgcolor: colors.white,
    },
    permissionModuleTitleCell: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      minWidth: 0,
    },
    permissionModuleTitle: {
      fontWeight: 700,
      color: accent.textPrimary ?? colors.grey[900],
    },
    permissionModuleViewCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    permissionModuleGroup: {
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      '&:last-child': { borderBottom: 'none' },
    },
    permissionModuleGroupHeader: {
      display: 'grid',
      alignItems: 'center',
      columnGap: 1,
      px: 2,
      py: 1,
      minWidth: { xs: 640, md: 760 },
      bgcolor: colors.grey[50],
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
    },
    permissionModuleGroupTitle: {
      fontWeight: 600,
      color: accent.textPrimary ?? colors.grey[800],
      pl: 0.5,
    },
    permissionsActions: {
      display: 'flex',
      gap: 1.5,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      pt: 0,
      mb: 2,
    },
    clonePreviewSection: {
      mt: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      minHeight: 0,
    },
    clonePreviewHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    },
    clonePreviewTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
    },
    clonePreviewTitle: {
      fontWeight: 600,
      color: accent.textPrimary ?? colors.grey[900],
    },
    clonePreviewDescription: {
      color: accent.textSecondary ?? colors.grey[700],
      fontSize: '0.875rem',
      lineHeight: 1.45,
    },
    clonePreviewLoading: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      py: 2,
    },
    clonePreviewAlert: {
      mb: 0,
    },
    clonePreviewScroll: {
      maxHeight: { xs: 320, sm: 420, md: 480 },
      overflowY: 'auto',
      overflowX: 'hidden',
      pr: 0.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: 0,
      bgcolor: colors.white,
      p: 1.5,
    },
    deleteGuardLoading: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      py: 1,
    },
    deleteGuardAlert: {
      mb: 0,
    },
    deleteAssignedUsersList: {
      m: 0,
      pl: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      color: accent.textSecondary ?? colors.grey[700],
      fontSize: '0.875rem',
    },
  };
}
